import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../scss/CraneSubDashboard.scss';
import BaseURL from '../assets/contants/BaseURL';
import { useNavigate } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin
);

const CraneSubDashboard = () => {
    const navigate = useNavigate();
    const [craneInfo, setCraneInfo] = useState({
        name: '',
        status: '',
        machineId: ''
    });
    const [metrics, setMetrics] = useState({
        cumulative_hours: 0,
        live_status: 'Unknown',
        non_working_time: 0,
        duty_cycle: 0,
        connection_status: 'offline'
    });
    const [motorStatus, setMotorStatus] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [activityTimeline, setActivityTimeline] = useState([]);
    const [ganttData, setGanttData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [dateRange, setDateRange] = useState('today');
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [chartData, setChartData] = useState({
        labels: [],
        loadData: [],
        capacityData: [],
        energyData: []
    });
    const [isLoading, setIsLoading] = useState(true);

    // Refs for WebSockets
    const analyticsWebSocketRef = useRef(null);
    const dashboardWebSocketRef = useRef(null);
    const chartsWebSocketRef = useRef(null);
    const ganttWebSocketRef = useRef(null);

    // Refs for reconnect timeouts and retry counts
    const reconnectTimeoutsRef = useRef({
        analytics: null,
        dashboard: null,
        charts: null,
        gantt: null
    });

    const reconnectAttemptsRef = useRef({
        analytics: 0,
        dashboard: 0,
        charts: 0,
        gantt: 0
    });

    const loadChartRef = useRef(null);
    const energyChartRef = useRef(null);
    const isUnmountingRef = useRef(false);

    // Fix: Get machineId from URL properly (support both query string and hash)
    const getMachineId = useCallback(() => {
        // Check both window.location.search and window.location.hash
        let queryString = window.location.search;
        
        if (!queryString && window.location.hash) {
            const hashParts = window.location.hash.split('?');
            if (hashParts.length > 1) {
                queryString = '?' + hashParts[1];
            }
        }
        
        const urlParams = new URLSearchParams(queryString);
        
        // Try multiple parameter names
        const machineId = urlParams.get('machineId') || 
                         urlParams.get('crane') || 
                         urlParams.get('id');
        
        if (!machineId) {
            console.error('No machine ID found in URL parameters');
            return null;
        }
        
        return machineId;
    }, []);

    const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');
    const getSessionID = () => sessionStorage.getItem('session_id') || localStorage.getItem('session_id');

    // Fix: Initialize crane info from URL params when component mounts
    useEffect(() => {
        // Parse URL parameters
        let queryString = window.location.search;
        
        // Also check hash for parameters (for hash-based routing)
        if (!queryString && window.location.hash) {
            const hashParts = window.location.hash.split('?');
            if (hashParts.length > 1) {
                queryString = '?' + hashParts[1];
            }
        }
        
        const urlParams = new URLSearchParams(queryString);
        
        const machineId = urlParams.get('machineId') || urlParams.get('crane');
        const craneName = urlParams.get('name') ? decodeURIComponent(urlParams.get('name')) : '';
        const craneStatus = urlParams.get('status') || '';
        
        if (!machineId) {
            console.error('Missing machine ID in URL');
            showNotification('Machine ID not found. Redirecting...', 'error');
            setTimeout(() => navigate('/dashboard'), 2000);
            return;
        }
        
        setCraneInfo({
            name: craneName,
            status: craneStatus,
            machineId: machineId
        });
        
        setIsLoading(false);
    }, [navigate]);

    // Rest of your existing code...
    const formatDuration = (minutes) => {
        if (!minutes && minutes !== 0) return '0m';
        if (minutes < 0.01667) {
            const seconds = Math.round(minutes * 60);
            return `${seconds}s`;
        }
        if (minutes < 60) {
            const mins = Math.floor(minutes);
            const secs = Math.round((minutes - mins) * 60);
            if (secs === 0) return `${mins}m`;
            return `${mins}m ${secs}s`;
        }
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        if (mins === 0) return `${hours}h`;
        return `${hours}h ${mins}m`;
    };

    const getTimeAgo = (timestamp) => {
        const now = new Date();
        const diffMs = now - timestamp;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

        return timestamp.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const showNotification = (message, type = 'info') => {
        console.log(`[${type.toUpperCase()}] ${message}`);
    };

    const getStatusClass = (status) => {
        const lowerStatus = (status || '').toLowerCase();
        switch (lowerStatus) {
            case 'working': return 'status-working';
            case 'idle': return 'status-idle';
            case 'off': return 'status-off';
            case 'overload': return 'status-overload';
            case 'error': return 'status-error';
            case 'emergency': return 'status-emergency';
            default: return 'status-off';
        }
    };

    const clearReconnectTimeout = (type) => {
        if (reconnectTimeoutsRef.current[type]) {
            clearTimeout(reconnectTimeoutsRef.current[type]);
            reconnectTimeoutsRef.current[type] = null;
        }
    };

    const getWebSocketUrl = (endpoint) => {
        const token = getAuthToken();
        const session_id = getSessionID();
        const machineId = getMachineId();

        if (!token || !session_id) {
            console.warn(`Missing auth credentials for ${endpoint} WebSocket`);
            return null;
        }

        if (!machineId) {
            console.warn(`Missing machine ID for ${endpoint} WebSocket`);
            return null;
        }

        let host = BaseURL;
        if (BaseURL.includes('://')) {
            host = BaseURL.split('://')[1];
        }
        if (host.endsWith('/')) {
            host = host.slice(0, -1);
        }

        const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${wsScheme}//${host}/data/${endpoint}/?token=${encodeURIComponent(token)}&session_id=${encodeURIComponent(session_id)}&machine_id=${encodeURIComponent(machineId)}`;
    };

    const sendSubscription = (ws, type, machineId, dateRange = null) => {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            console.warn(`Cannot send subscription for ${type}: WebSocket not open`);
            return false;
        }

        let message;
        switch (type) {
            case 'analytics':
                message = {
                    type: 'subscribe',
                    machine_id: machineId,
                    date_range: dateRange
                };
                break;
            case 'dashboard':
                message = {
                    type: 'subscribe_dashboard',
                    machine_id: machineId,
                    date_range: dateRange
                };
                break;
            case 'charts':
                message = {
                    type: 'subscribe_charts',
                    machine_id: machineId,
                    date_range: dateRange
                };
                break;
            case 'gantt':
                message = {
                    type: 'get_status_data',
                    crane_id: machineId,
                    date: dateRange
                };
                break;
            default:
                return false;
        }

        try {
            ws.send(JSON.stringify(message));
            console.log(`Subscribed to ${type} for machine ${machineId}`);
            return true;
        } catch (error) {
            console.error(`Error sending subscription for ${type}:`, error);
            return false;
        }
    };

    const connectToAnalyticsWebSocket = useCallback(() => {
        if (isUnmountingRef.current || !craneInfo.machineId) return;

        try {
            const machineId = craneInfo.machineId;
            const wsUrl = getWebSocketUrl('sub-crane-analytics');

            if (!wsUrl) return;

            if (analyticsWebSocketRef.current) {
                if (analyticsWebSocketRef.current.readyState === WebSocket.OPEN ||
                    analyticsWebSocketRef.current.readyState === WebSocket.CONNECTING) {
                    return;
                }
                analyticsWebSocketRef.current.close();
            }

            analyticsWebSocketRef.current = new WebSocket(wsUrl);
            let connectionTimeout = setTimeout(() => {
                if (analyticsWebSocketRef.current && analyticsWebSocketRef.current.readyState !== WebSocket.OPEN) {
                    console.error('Analytics WebSocket connection timeout');
                    analyticsWebSocketRef.current.close();
                }
            }, 10000);

            analyticsWebSocketRef.current.onopen = () => {
                clearTimeout(connectionTimeout);
                reconnectAttemptsRef.current.analytics = 0;
                setTimeout(() => {
                    sendSubscription(analyticsWebSocketRef.current, 'analytics', machineId, dateRange);
                }, 500);
            };

            analyticsWebSocketRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'pong') return;

                    if (data.type === 'metrics' || data.type === 'metrics_update') {
                        if (data.metrics) {
                            setMetrics(prev => ({ ...prev, ...data.metrics }));
                            if (data.metrics.last_updated_time) {
                                setLastUpdated(new Date(data.metrics.last_updated_time));
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error parsing analytics message:', error);
                }
            };

            analyticsWebSocketRef.current.onerror = (error) => {
                console.error('Analytics WebSocket error:', error);
            };

            analyticsWebSocketRef.current.onclose = (event) => {
                clearTimeout(connectionTimeout);

                if (!isUnmountingRef.current && reconnectAttemptsRef.current.analytics < 5) {
                    const delay = Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current.analytics), 30000);

                    clearReconnectTimeout('analytics');
                    reconnectTimeoutsRef.current.analytics = setTimeout(() => {
                        reconnectAttemptsRef.current.analytics++;
                        connectToAnalyticsWebSocket();
                    }, delay);
                } else if (reconnectAttemptsRef.current.analytics >= 5) {
                    console.error('Analytics WebSocket max reconnection attempts reached');
                }
            };

        } catch (error) {
            console.error('Error creating Analytics WebSocket:', error);
            if (!isUnmountingRef.current) {
                clearReconnectTimeout('analytics');
                reconnectTimeoutsRef.current.analytics = setTimeout(() => {
                    connectToAnalyticsWebSocket();
                }, 5000);
            }
        }
    }, [craneInfo.machineId, dateRange]);

    const connectToDashboardWebSocket = useCallback(() => {
        if (isUnmountingRef.current || !craneInfo.machineId) return;

        try {
            const machineId = craneInfo.machineId;
            const wsUrl = getWebSocketUrl('sub-crane-dashboard');

            if (!wsUrl) return;

            if (dashboardWebSocketRef.current) {
                if (dashboardWebSocketRef.current.readyState === WebSocket.OPEN ||
                    dashboardWebSocketRef.current.readyState === WebSocket.CONNECTING) {
                    return;
                }
                dashboardWebSocketRef.current.close();
            }

            dashboardWebSocketRef.current = new WebSocket(wsUrl);
            let connectionTimeout = setTimeout(() => {
                if (dashboardWebSocketRef.current && dashboardWebSocketRef.current.readyState !== WebSocket.OPEN) {
                    console.error('Dashboard WebSocket connection timeout');
                    dashboardWebSocketRef.current.close();
                }
            }, 10000);

            dashboardWebSocketRef.current.onopen = () => {
                clearTimeout(connectionTimeout);
                reconnectAttemptsRef.current.dashboard = 0;
                setTimeout(() => {
                    sendSubscription(dashboardWebSocketRef.current, 'dashboard', machineId, dateRange);
                }, 500);
            };

            dashboardWebSocketRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'pong') return;

                    switch (data.type) {
                        case 'initial_data':
                            if (data.activity_timeline) {
                                setActivityTimeline(data.activity_timeline.activity_timeline || []);
                            }
                            if (data.motor_status) {
                                setMotorStatus(data.motor_status.motor_status || []);
                            }
                            if (data.active_alerts) {
                                setAlerts(data.active_alerts.active_alerts || []);
                            }
                            break;
                        case 'activity_timeline':
                            if (data.data && data.data.activity_timeline) {
                                setActivityTimeline(data.data.activity_timeline);
                            }
                            break;
                        case 'motor_status':
                            if (data.data) {
                                setMotorStatus(data.data.motor_status || []);
                            }
                            break;
                        case 'active_alerts':
                            if (data.data && data.data.active_alerts) {
                                setAlerts(data.data.active_alerts);
                            }
                            break;
                        default:
                            break;
                    }
                } catch (error) {
                    console.error('Error parsing dashboard message:', error);
                }
            };

            dashboardWebSocketRef.current.onerror = (error) => {
                console.error('Dashboard WebSocket error:', error);
            };

            dashboardWebSocketRef.current.onclose = (event) => {
                clearTimeout(connectionTimeout);

                if (!isUnmountingRef.current && reconnectAttemptsRef.current.dashboard < 5) {
                    const delay = Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current.dashboard), 30000);

                    clearReconnectTimeout('dashboard');
                    reconnectTimeoutsRef.current.dashboard = setTimeout(() => {
                        reconnectAttemptsRef.current.dashboard++;
                        connectToDashboardWebSocket();
                    }, delay);
                } else if (reconnectAttemptsRef.current.dashboard >= 5) {
                    console.error('Dashboard WebSocket max reconnection attempts reached');
                }
            };

        } catch (error) {
            console.error('Error creating Dashboard WebSocket:', error);
            if (!isUnmountingRef.current) {
                clearReconnectTimeout('dashboard');
                reconnectTimeoutsRef.current.dashboard = setTimeout(() => {
                    connectToDashboardWebSocket();
                }, 5000);
            }
        }
    }, [craneInfo.machineId, dateRange]);

    const connectToChartsWebSocket = useCallback(() => {
        if (isUnmountingRef.current || !craneInfo.machineId) return;

        try {
            const machineId = craneInfo.machineId;
            const wsUrl = getWebSocketUrl('sub-crane-charts');

            if (!wsUrl) return;

            if (chartsWebSocketRef.current) {
                if (chartsWebSocketRef.current.readyState === WebSocket.OPEN ||
                    chartsWebSocketRef.current.readyState === WebSocket.CONNECTING) {
                    return;
                }
                chartsWebSocketRef.current.close();
            }

            chartsWebSocketRef.current = new WebSocket(wsUrl);
            let connectionTimeout = setTimeout(() => {
                if (chartsWebSocketRef.current && chartsWebSocketRef.current.readyState !== WebSocket.OPEN) {
                    console.error('Charts WebSocket connection timeout');
                    chartsWebSocketRef.current.close();
                }
            }, 10000);

            chartsWebSocketRef.current.onopen = () => {
                clearTimeout(connectionTimeout);
                reconnectAttemptsRef.current.charts = 0;
                setTimeout(() => {
                    sendSubscription(chartsWebSocketRef.current, 'charts', machineId, dateRange);
                }, 500);
            };

            chartsWebSocketRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'pong') return;

                    if (data.type === 'chart_data' && data.data) {
                        const chartDataObj = data.data;
                        if (chartDataObj.labels && chartDataObj.datasets) {
                            const loadDataset = chartDataObj.datasets.find(d => d.label === 'Load (kg)');
                            const capacityDataset = chartDataObj.datasets.find(d => d.label === 'Capacity (kg)');
                            const energyDataset = chartDataObj.datasets.find(d => d.label === 'Energy (kWh)');

                            setChartData({
                                labels: chartDataObj.labels,
                                loadData: loadDataset?.data || [],
                                capacityData: capacityDataset?.data || [],
                                energyData: energyDataset?.data || []
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error parsing charts message:', error);
                }
            };

            chartsWebSocketRef.current.onerror = (error) => {
                console.error('Charts WebSocket error:', error);
            };

            chartsWebSocketRef.current.onclose = (event) => {
                clearTimeout(connectionTimeout);

                if (!isUnmountingRef.current && reconnectAttemptsRef.current.charts < 5) {
                    const delay = Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current.charts), 30000);

                    clearReconnectTimeout('charts');
                    reconnectTimeoutsRef.current.charts = setTimeout(() => {
                        reconnectAttemptsRef.current.charts++;
                        connectToChartsWebSocket();
                    }, delay);
                } else if (reconnectAttemptsRef.current.charts >= 5) {
                    console.error('Charts WebSocket max reconnection attempts reached');
                }
            };

        } catch (error) {
            console.error('Error creating Charts WebSocket:', error);
            if (!isUnmountingRef.current) {
                clearReconnectTimeout('charts');
                reconnectTimeoutsRef.current.charts = setTimeout(() => {
                    connectToChartsWebSocket();
                }, 5000);
            }
        }
    }, [craneInfo.machineId, dateRange]);

    const connectToGanttWebSocket = useCallback(() => {
        if (isUnmountingRef.current || !craneInfo.machineId) return;

        try {
            const wsUrl = getWebSocketUrl('sub-crane-status-gantt');

            if (!wsUrl) return;

            if (ganttWebSocketRef.current) {
                if (ganttWebSocketRef.current.readyState === WebSocket.OPEN ||
                    ganttWebSocketRef.current.readyState === WebSocket.CONNECTING) {
                    return;
                }
                ganttWebSocketRef.current.close();
            }

            ganttWebSocketRef.current = new WebSocket(wsUrl);
            let connectionTimeout = setTimeout(() => {
                if (ganttWebSocketRef.current && ganttWebSocketRef.current.readyState !== WebSocket.OPEN) {
                    console.error('Gantt WebSocket connection timeout');
                    ganttWebSocketRef.current.close();
                }
            }, 10000);

            ganttWebSocketRef.current.onopen = () => {
                clearTimeout(connectionTimeout);
                reconnectAttemptsRef.current.gantt = 0;
                setTimeout(() => {
                    sendSubscription(ganttWebSocketRef.current, 'gantt', craneInfo.machineId, selectedDate);
                }, 500);
            };

            ganttWebSocketRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'pong') return;

                    if (data.type === 'status_data' || data.type === 'date_range_status_data') {
                        if (data.data && !data.data.error) {
                            setGanttData(prevData => {
                                if (JSON.stringify(prevData) === JSON.stringify(data.data)) {
                                    return prevData;
                                }
                                return data.data;
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error parsing gantt message:', error);
                }
            };

            ganttWebSocketRef.current.onerror = (error) => {
                console.error('Gantt WebSocket error:', error);
            };

            ganttWebSocketRef.current.onclose = (event) => {
                clearTimeout(connectionTimeout);

                if (!isUnmountingRef.current && reconnectAttemptsRef.current.gantt < 5) {
                    const delay = Math.min(10000 * Math.pow(2, reconnectAttemptsRef.current.gantt), 60000);

                    clearReconnectTimeout('gantt');
                    reconnectTimeoutsRef.current.gantt = setTimeout(() => {
                        reconnectAttemptsRef.current.gantt++;
                        connectToGanttWebSocket();
                    }, delay);
                } else if (reconnectAttemptsRef.current.gantt >= 5) {
                    console.error('Gantt WebSocket max reconnection attempts reached');
                }
            };

        } catch (error) {
            console.error('Error creating Gantt WebSocket:', error);
            if (!isUnmountingRef.current) {
                clearReconnectTimeout('gantt');
                reconnectTimeoutsRef.current.gantt = setTimeout(() => {
                    connectToGanttWebSocket();
                }, 10000);
            }
        }
    }, [craneInfo.machineId, selectedDate]);

    // Initial connection effect
    useEffect(() => {
        if (!craneInfo.machineId) return;
        
        isUnmountingRef.current = false;

        const connectAll = () => {
            connectToAnalyticsWebSocket();
            connectToDashboardWebSocket();
            connectToChartsWebSocket();
            connectToGanttWebSocket();
        };

        connectAll();

        return () => {
            isUnmountingRef.current = true;

            // Close all WebSockets
            const sockets = [analyticsWebSocketRef, dashboardWebSocketRef, chartsWebSocketRef, ganttWebSocketRef];
            sockets.forEach(socketRef => {
                if (socketRef.current) {
                    socketRef.current.close();
                    socketRef.current = null;
                }
            });

            // Clear all timeouts
            Object.keys(reconnectTimeoutsRef.current).forEach(key => {
                if (reconnectTimeoutsRef.current[key]) {
                    clearTimeout(reconnectTimeoutsRef.current[key]);
                }
            });
        };
    }, [craneInfo.machineId, connectToAnalyticsWebSocket, connectToDashboardWebSocket, connectToChartsWebSocket, connectToGanttWebSocket]);

    // Refresh data when dateRange changes
    useEffect(() => {
        if (!craneInfo.machineId) return;
        
        const refreshTimeout = setTimeout(() => {
            if (dashboardWebSocketRef.current?.readyState === WebSocket.OPEN) {
                sendSubscription(dashboardWebSocketRef.current, 'dashboard', craneInfo.machineId, dateRange);
            }
            if (chartsWebSocketRef.current?.readyState === WebSocket.OPEN) {
                sendSubscription(chartsWebSocketRef.current, 'charts', craneInfo.machineId, dateRange);
            }
        }, 500);

        return () => clearTimeout(refreshTimeout);
    }, [dateRange, craneInfo.machineId]);

    // Refresh Gantt data when selectedDate changes
    useEffect(() => {
        if (!craneInfo.machineId) return;
        
        const refreshTimeout = setTimeout(() => {
            if (ganttWebSocketRef.current?.readyState === WebSocket.OPEN) {
                sendSubscription(ganttWebSocketRef.current, 'gantt', craneInfo.machineId, selectedDate);
            }
        }, 500);

        return () => clearTimeout(refreshTimeout);
    }, [selectedDate, craneInfo.machineId]);

    const loadChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            tooltip: { mode: 'index', intersect: false },
            zoom: {
                zoom: {
                    wheel: { enabled: true, modifierKey: 'ctrl' },
                    pinch: { enabled: true },
                    mode: 'x'
                },
                pan: { enabled: true, mode: 'x', modifierKey: 'ctrl' }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Load (kg)' }
            }
        }
    };

    const energyChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            tooltip: { mode: 'index', intersect: false },
            zoom: {
                zoom: {
                    wheel: { enabled: true, modifierKey: 'ctrl' },
                    pinch: { enabled: true },
                    mode: 'x'
                },
                pan: { enabled: true, mode: 'x', modifierKey: 'ctrl' }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Load (kg)' }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: { display: true, text: 'Energy (kWh)' },
                beginAtZero: true,
                grid: { drawOnChartArea: false }
            }
        }
    };

    const loadChartData = {
        labels: chartData.labels,
        datasets: [
            {
                label: 'Load (kg)',
                data: chartData.loadData,
                borderColor: '#3498db',
                backgroundColor: '#3498db20',
                borderWidth: 2,
                tension: 0.1,
                fill: true,
                pointRadius: 2,
                pointHoverRadius: 5,
                yAxisID: 'y'
            },
            {
                label: 'Capacity (kg)',
                data: chartData.capacityData,
                borderColor: '#e74c3c',
                backgroundColor: '#e74c3c20',
                borderWidth: 2,
                borderDash: [5, 5],
                fill: false,
                pointRadius: 0,
                yAxisID: 'y'
            }
        ]
    };

    const energyChartData = {
        labels: chartData.labels,
        datasets: [
            {
                label: 'Load (kg)',
                data: chartData.loadData,
                borderColor: '#3498db',
                backgroundColor: '#3498db20',
                borderWidth: 2,
                tension: 0.1,
                fill: false,
                pointRadius: 2,
                pointHoverRadius: 4,
                yAxisID: 'y'
            },
            {
                label: 'Energy (kWh)',
                data: chartData.energyData,
                borderColor: '#f39c12',
                backgroundColor: '#f39c1220',
                borderWidth: 2,
                tension: 0.1,
                fill: false,
                pointRadius: 2,
                pointHoverRadius: 4,
                yAxisID: 'y1'
            }
        ]
    };

    const GanttChart = ({ data, selectedDate: date }) => {
        if (!data || !data.items || data.items.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    <i className="fas fa-chart-bar" style={{ fontSize: '2rem', marginBottom: '8px' }}></i>
                    <p style={{ fontSize: '0.8rem' }}>No data available</p>
                </div>
            );
        }

        const targetDate = new Date(data.items[0].start_time);
        const rangeStart = new Date(targetDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(targetDate);
        rangeEnd.setHours(23, 59, 59, 999);
        const totalDuration = rangeEnd - rangeStart;

        const getStatusClass = (status) => {
            switch (status?.toLowerCase()) {
                case 'working': return 'working';
                case 'idle': return 'idle';
                case 'stop': return 'stop';
                case 'overload': return 'overload';
                default: return 'offline';
            }
        };

        const sortedItems = [...data.items].sort((a, b) =>
            new Date(a.start_time) - new Date(b.start_time)
        );

        const today = new Date();
        const showNowIndicator = date === today.toISOString().split('T')[0];

        return (
            <div>
                <div style={{ position: 'relative', minHeight: '100px' }}>
                    <div style={{ position: 'relative', height: '60px', background: '#fafafa', borderRadius: '4px', marginBottom: '20px' }}>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                            {[...Array(25)].map((_, i) => (
                                <div key={i} style={{ flex: 1, borderLeft: i > 0 ? '1px solid #f0f0f0' : 'none' }}></div>
                            ))}
                        </div>
                        <div style={{ position: 'absolute', inset: 0 }}>
                            {sortedItems.map((item, idx) => {
                                const startTime = new Date(item.start_time);
                                const endTime = item.end_time ? new Date(item.end_time) : new Date();
                                let startPosition = Math.max(0, (startTime - rangeStart) / totalDuration * 100);
                                let endPosition = Math.min(100, (endTime - rangeStart) / totalDuration * 100);
                                let width = endPosition - startPosition;

                                if (width <= 0) return null;

                                return (
                                    <div
                                        key={idx}
                                        className={`gantt-bar ${getStatusClass(item.status)}`}
                                        style={{
                                            position: 'absolute',
                                            left: `${startPosition}%`,
                                            width: `${width}%`,
                                            height: '40px',
                                            top: '10px',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                        title={`${item.status} | ${startTime.toLocaleTimeString()} - ${endTime.toLocaleTimeString()}`}
                                    />
                                );
                            })}
                        </div>
                        {showNowIndicator && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                width: '2px',
                                background: '#3498db',
                                zIndex: 5,
                                left: `${((new Date() - rangeStart) / totalDuration) * 100}%`
                            }} />
                        )}
                    </div>
                    <div style={{ position: 'relative', height: '20px', marginTop: '4px', borderTop: '1px solid #eee' }}>
                        {[...Array(13)].map((_, i) => {
                            const hour = i * 2;
                            const markerTime = new Date(rangeStart.getTime() + (hour * 60 * 60 * 1000));
                            return (
                                <div
                                    key={i}
                                    style={{
                                        position: 'absolute',
                                        left: `${(hour / 24) * 100}%`,
                                        transform: 'translateX(-50%)',
                                        fontSize: '0.6rem',
                                        color: '#999',
                                        fontWeight: hour % 4 === 0 ? 600 : 400
                                    }}
                                >
                                    {markerTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '16px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee', fontSize: '0.75rem' }}>
                    <div><span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#2ecc71', borderRadius: '2px', marginRight: '4px' }}></span> Working: {formatDuration(data.total_working_minutes || 0)}</div>
                    <div><span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#f39c12', borderRadius: '2px', marginRight: '4px' }}></span> Idle: {formatDuration(data.total_idle_minutes || 0)}</div>
                    <div><span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#95a5a6', borderRadius: '2px', marginRight: '4px' }}></span> Stop: {formatDuration(data.total_stop_minutes || 0)}</div>
                    <div><span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#e74c3c', borderRadius: '2px', marginRight: '4px' }}></span> Overload: {formatDuration(data.total_overload_minutes || 0)}</div>
                    <div style={{ marginLeft: 'auto' }}><i className="far fa-clock" style={{ marginRight: '4px' }}></i> Total: {formatDuration(
                        (data.total_working_minutes || 0) + (data.total_idle_minutes || 0) +
                        (data.total_stop_minutes || 0) + (data.total_overload_minutes || 0) +
                        (data.total_offline_minutes || 0)
                    )}</div>
                </div>
            </div>
        );
    };

    const handleDateChange = (direction) => {
        const currentDate = new Date(selectedDate);
        currentDate.setDate(currentDate.getDate() + direction);
        const today = new Date();
        if (direction === 1 && currentDate > today) {
            showNotification('Cannot select future dates', 'warning');
            return;
        }
        setSelectedDate(currentDate.toISOString().split('T')[0]);
    };

    const connectionStatusClass = metrics.connection_status === 'online' ? 'connected' : 'disconnected';
    const connectionStatusText = metrics.connection_status === 'online' ? 'Connected' : 'Disconnected';

    return (
        <div className="dashboard-content">
            <div className="back-button" style={{ marginBottom: '24px' }}>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{ color: 'inherit', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                    <FontAwesomeIcon icon={faAngleLeft} /> Dashboard
                </button>
            </div>

            <div className="crane-header" style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <h2 className="crane-title">{craneInfo.name}</h2>
                    <span className={`crane-status ${getStatusClass(metrics.live_status)}`}>
                        {metrics.live_status}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div className="connection-status">
                        <span className={`connection-indicator ${connectionStatusClass}`}></span>
                        <span className="connection-text">{connectionStatusText}</span>
                    </div>

                    <div style={{ fontSize: '13px', color: '#95a5a6', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <i className="fas fa-clock" style={{ fontSize: '11px' }}></i>
                        <span>Last updated: {lastUpdated.toLocaleString()}</span>
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
                <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="fas fa-chart-gantt" style={{ color: '#3498db' }}></i> Operating States
                        </h3>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                    onClick={() => handleDateChange(-1)}
                                    style={{ padding: '4px 8px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                    ←
                                </button>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.7rem', width: '120px' }}
                                />
                                <button
                                    onClick={() => handleDateChange(1)}
                                    style={{ padding: '4px 8px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                    →
                                </button>
                            </div>
                        </div>
                    </div>
                    <GanttChart data={ganttData} selectedDate={selectedDate} />
                </div>
            </div>

            <div className="layout-main">
                <div className="charts-col">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#2c3e50' }}>Key Metrics</h3>
                        </div>
                    </div>

                    <div className="key-metrics-section">
                        <div className="metric-card key-metric">
                            <div className="metric-title"><i className="fas fa-clock"></i> Cumulative Hours</div>
                            <div className="metric-value">{metrics.cumulative_hours}<span className="metric-unit">hrs</span></div>
                            <div className="metric-subtext">Today</div>
                        </div>

                        <div className="metric-card key-metric">
                            <div className="metric-title"><i className="fas fa-play-circle"></i> Live Status</div>
                            <div className={`${getStatusClass(metrics.live_status)}`}>
                                {metrics.live_status === 'WORKING' ? 'Operating' : metrics.live_status}
                            </div>
                            <div className="metric-subtext">
                                {metrics.live_status === 'WORKING' ? 'Currently: Lifting' :
                                    metrics.live_status === 'IDLE' ? 'Powered on, not moving' :
                                        metrics.live_status === 'OVERLOAD' ? '⚠️ Load exceeds threshold' : 'Status unknown'}
                            </div>
                        </div>

                        <div className="metric-card key-metric">
                            <div className="metric-title"><i className="fas fa-stop-circle"></i> Non-Working Time</div>
                            <div className="metric-value">{metrics.non_working_time}<span className="metric-unit">hrs</span></div>
                            <div className="metric-subtext">Today</div>
                        </div>

                        <div className="metric-card key-metric">
                            <div className="metric-title"><i className="fas fa-tachometer-alt"></i> Duty Cycle</div>
                            <div className="metric-value">{metrics.duty_cycle}<span className="metric-unit">%</span></div>
                            <div className="metric-subtext">Efficiency</div>
                        </div>
                    </div>

                    <div className="charts-grid-two" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', marginTop: '20px' }}>
                        <div className="chartcontainer">
                            <div className="chart-header">
                                <h3 className="card-title"><i className="fas fa-weight-hanging"></i> Load vs Capacity</h3>
                            </div>
                            <div style={{ height: '300px' }}>
                                <Line ref={loadChartRef} data={loadChartData} options={loadChartOptions} />
                            </div>
                        </div>

                        <div className="chartcontainer">
                            <div className="chart-header">
                                <h3 className="card-title"><i className="fas fa-bolt"></i> Energy vs Load</h3>
                            </div>
                            <div style={{ height: '300px' }}>
                                <Line ref={energyChartRef} data={energyChartData} options={energyChartOptions} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="metrics-col">
                    <div className="metrics-grid-cards-right">
                        <div className="filter-group">
                            <label className="filter-label">Date Range</label>
                            <select
                                className="filter-select"
                                value={dateRange}
                                onChange={(e) => setDateRange(e.target.value)}
                            >
                                <option value="today">Today</option>
                                <option value="week">Last 7 Days</option>
                                <option value="month">Last 30 Days</option>
                            </select>
                        </div>

                        <div className="timeline-panel">
                            <h4><i className="fas fa-stream"></i> Activity Timeline</h4>
                            <ul className="timeline">
                                {activityTimeline.length === 0 ? (
                                    <li><span className="status-dot dot-off"></span><div><strong>No recent activity</strong></div></li>
                                ) : (
                                    activityTimeline.map((activity, idx) => {
                                        const timestamp = new Date(activity.created_at || activity.timestamp);
                                        const timeString = timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                                        return (
                                            <li key={idx}>
                                                <span className={`status-dot ${activity.dot_class || 'dot-working'}`}></span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <i className={activity.icon_class || 'fas fa-info-circle'} style={{ fontSize: '12px', color: '#95a5a6' }}></i>
                                                    <span><strong>{timeString}</strong> - {activity.activity_text || activity.type}</span>
                                                </div>
                                            </li>
                                        );
                                    })
                                )}
                            </ul>
                        </div>

                        <div className="status-container">
                            <h3 className="status-header"><i className="fas fa-cogs"></i> Motor Status</h3>
                            <div className="status-grid">
                                {motorStatus.length === 0 ? (
                                    <div className="status-item">
                                        <div className="status-name"><span className="status-dot dot-off"></span><span>No motor data</span></div>
                                        <div className="status-value">Unknown</div>
                                        <div className="status-info">No data available</div>
                                    </div>
                                ) : (
                                    motorStatus.map((motor, idx) => {
                                        let dotClass = 'dot-off';
                                        let statusText = 'Unknown';
                                        switch (motor.status) {
                                            case 'running': dotClass = 'dot-working'; statusText = 'Running'; break;
                                            case 'idle': dotClass = 'dot-idle'; statusText = 'Idle'; break;
                                            case 'stopped': dotClass = 'dot-off'; statusText = 'Stopped'; break;
                                            default: dotClass = 'dot-off'; statusText = 'Unknown';
                                        }
                                        return (
                                            <div key={idx} className="status-item">
                                                <div className="status-name">
                                                    <span className={`status-dot ${dotClass}`}></span>
                                                    <span>{motor.motor_type?.toUpperCase() || 'Unknown'}</span>
                                                </div>
                                                <div className="status-value">{statusText}</div>
                                                <div className="status-info">{motor.energy_data?.power || 'N/A'} W</div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div className="alerts-container">
                            <div className="alerts-header">
                                <h3 className="alerts-title"><i className="fas fa-exclamation-triangle"></i> Active Alerts</h3>
                                <div className="alerts-count">{alerts.length}</div>
                            </div>
                            <div className="alerts-list">
                                {alerts.length === 0 ? (
                                    <div className="alert-item">
                                        <div className="alert-icon" style={{ backgroundColor: '#4caf50', color: 'white' }}>
                                            <i className="fas fa-check-circle"></i>
                                        </div>
                                        <div className="alert-text">No Active Alerts</div>
                                        <div className="alert-time">All systems normal</div>
                                    </div>
                                ) : (
                                    alerts.map((alert, idx) => {
                                        let iconClass = 'alert-icon-warning';
                                        let icon = 'fas fa-exclamation-circle';
                                        const timestamp = new Date(alert.data_timestamp || alert.timestamp);
                                        const timeAgo = getTimeAgo(timestamp);

                                        switch (alert.type) {
                                            case 'EMERGENCY_STOP': iconClass = 'alert-icon-danger'; icon = 'fas fa-exclamation-triangle'; break;
                                            case 'MOTOR_FAULT': iconClass = 'alert-icon-danger'; icon = 'fas fa-cogs'; break;
                                            case 'MOTOR_OVERCURRENT': iconClass = 'alert-icon-danger'; icon = 'fas fa-bolt'; break;
                                            default: iconClass = 'alert-icon-warning'; icon = 'fas fa-exclamation-circle';
                                        }

                                        return (
                                            <div key={idx} className="alert-item">
                                                <div className={`alert-icon ${iconClass}`}>
                                                    <i className={icon}></i>
                                                </div>
                                                <div className="alert-text">{alert.message}</div>
                                                <div className="alert-time">{timeAgo}</div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CraneSubDashboard;